/**
 * Cross-tile terrain vertex-normal reconciliation (issue #107).
 *
 * Each LOD1 tile's normals are computed per tile (computeVertexNormals over
 * that tile's own triangles — see terrainMaterial.computeTintColors), so a
 * vertex on a shared tile border carries a different normal in each adjacent
 * tile: each side only "sees" its own triangles. Under the flat-shaded
 * Stylised preset the normal attribute is ignored for lighting so this is
 * invisible, but under the smooth-shaded Natural preset (issue #106) the two
 * tiles disagree at the same border point and a lighting seam appears along
 * tile edges, worst at low sun angles. The same per-tile normals also feed the
 * slope tint (both presets), so border tint mismatches slightly too.
 *
 * This pass matches border vertices between adjacent loaded tiles by position
 * and gives every side one blended normal; the caller then refreshes the
 * affected tiles' vertex colours through the preset-aware tint path
 * (terrainMaterial.applyTerrainTint). It is a viewer-side, cosmetic pass:
 *
 *  - it only ever writes the 'normal' attribute; positions are never moved, so
 *    the collider and all LOS/ground results are untouched (hard constraint #1,
 *    #6). The pipeline's "no baked normals" contract and raypath/tin.py border
 *    welding are not touched.
 *  - a border vertex with no match within tolerance is left exactly as-is —
 *    positions are never nudged to force a match (hard constraint #7 spirit).
 *
 * Pure and three.js-geometry-only (no renderer), so it is unit-tested directly
 * in Node, following the RouteRibbon geometry-builder pattern. Never imports
 * from raypath/.
 *
 * Idempotent by construction: every reconciled group is set to the same
 * upward-canonicalised blended normal (y >= 0), so a re-run finds each member
 * already equal (to within float32 round-trip noise) and writes nothing.
 */
import type { BufferAttribute, BufferGeometry } from 'three';

/**
 * Position tolerance (metres) for treating two tiles' border vertices as the
 * same point. 2 cm covers the meshopt `-vpf` quantisation (measured < 5 mm per
 * the manifest's `tile_encoding` note) on top of the pipeline's exact welded
 * border heights (raypath/tin.py, AC-T4).
 */
export const BORDER_MATCH_TOLERANCE_M = 0.02;

/**
 * Below this summed-normal length a group is treated as degenerate — two (or
 * more) canonicalised normals that are near-horizontal and near-opposite, whose
 * sum cancels — and is left untouched (keep each side's own normal), rather
 * than emitting a not-finite or arbitrary direction.
 */
const DEGENERATE_SUM_EPS = 1e-6;

/**
 * A normal already within this per-component distance of a freshly-blended
 * value is treated as unchanged, so a re-run writes nothing (idempotency) and
 * an unchanged tile is not needlessly re-tinted. Comfortably above the float32
 * attribute round-trip noise a stored unit normal accrues.
 */
const ALREADY_EQUAL_EPS = 1e-6;

/** WeakMap cache of a geometry's border-vertex indices. Positions are immutable
 * for a tile's lifetime (this pass never moves them and the loader treats
 * position as byte-stable), so the border set is stable and safe to memoise;
 * entries drop automatically when a geometry is GC'd on tile unload. */
const borderCache = new WeakMap<BufferGeometry, { tol: number; indices: number[] }>();

/**
 * Indices of `geometry`'s vertices lying on its own x/z bounding-box edge
 * (within `tol`). Only these can coincide with a neighbour tile's vertices; a
 * tile's interior is >= a vertex spacing away from any other tile, so this
 * bounds the matching work to O(border vertices). Memoised per geometry.
 */
export function borderVertexIndices(geometry: BufferGeometry, tol = BORDER_MATCH_TOLERANCE_M): number[] {
  const cached = borderCache.get(geometry);
  if (cached && cached.tol === tol) return cached.indices;

  const pos = geometry.getAttribute('position');
  if (!pos) throw new Error('geometry has no position attribute');

  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  }

  const indices: number[] = [];
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    if (
      Math.abs(x - minX) <= tol ||
      Math.abs(x - maxX) <= tol ||
      Math.abs(z - minZ) <= tol ||
      Math.abs(z - maxZ) <= tol
    ) {
      indices.push(i);
    }
  }
  borderCache.set(geometry, { tol, indices });
  return indices;
}

interface BorderVertexRef {
  geometry: BufferGeometry;
  normal: BufferAttribute;
  index: number;
  x: number;
  y: number;
  z: number;
}

/**
 * Spatial hash of border vertices keyed by a (x, z) cell of side `tol`, so a
 * query point's coincident vertices are found by scanning only its 3x3 cell
 * neighbourhood (any point within `tol` lands at most one cell away on each
 * axis) and then verifying the true 3D distance.
 */
class BorderVertexHash {
  private readonly cells = new Map<string, BorderVertexRef[]>();
  private readonly tol: number;

  constructor(tol: number) {
    this.tol = tol;
  }

  private key(cx: number, cz: number): string {
    return `${cx},${cz}`;
  }

  insert(ref: BorderVertexRef): void {
    const cx = Math.floor(ref.x / this.tol);
    const cz = Math.floor(ref.z / this.tol);
    const k = this.key(cx, cz);
    const bucket = this.cells.get(k);
    if (bucket) bucket.push(ref);
    else this.cells.set(k, [ref]);
  }

  /** Every inserted vertex within `tol` (3D) of (x, y, z), including any at that
   * exact position (so a probe with a vertex that is itself in the hash returns
   * the whole coincident group). */
  query(x: number, y: number, z: number): BorderVertexRef[] {
    const cx = Math.floor(x / this.tol);
    const cz = Math.floor(z / this.tol);
    const out: BorderVertexRef[] = [];
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const bucket = this.cells.get(this.key(cx + dx, cz + dz));
        if (!bucket) continue;
        for (const ref of bucket) {
          if (Math.hypot(ref.x - x, ref.y - y, ref.z - z) <= this.tol) out.push(ref);
        }
      }
    }
    return out;
  }
}

function ensureNormals(geometry: BufferGeometry): BufferAttribute {
  if (!geometry.getAttribute('normal')) geometry.computeVertexNormals();
  return geometry.getAttribute('normal') as BufferAttribute;
}

/** Upward-facing representative of a normal: negate the whole vector when it
 * points down (y < 0), consistent with the TIN-winding abs() doctrine in
 * terrainMaterial.ts — the two represent the same surface orientation. */
function canonUp(x: number, y: number, z: number): [number, number, number] {
  return y < 0 ? [-x, -y, -z] : [x, y, z];
}

/**
 * Blended normal for a group of coincident border vertices: normalise the sum
 * of their upward-canonicalised normals. Returns null for the degenerate case
 * (sum not finite or near zero length), where the caller leaves each side's own
 * normal in place.
 */
function blendGroup(group: BorderVertexRef[]): [number, number, number] | null {
  let sx = 0;
  let sy = 0;
  let sz = 0;
  for (const ref of group) {
    const [cx, cy, cz] = canonUp(ref.normal.getX(ref.index), ref.normal.getY(ref.index), ref.normal.getZ(ref.index));
    sx += cx;
    sy += cy;
    sz += cz;
  }
  const len = Math.hypot(sx, sy, sz);
  if (!Number.isFinite(len) || len < DEGENERATE_SUM_EPS) return null;
  return [sx / len, sy / len, sz / len];
}

/**
 * Core pass: reconcile the border normals of the `pending` geometries against
 * the full `allLoaded` set (which must include the pending geometries). Builds
 * one border-vertex hash over `allLoaded`, then for each pending geometry's
 * border vertices gathers the coincident group across all tiles, blends, and
 * writes the single blended normal to every member. Returns the set of
 * geometries whose normal attribute actually changed (for the caller to refresh
 * their vertex colours). Positions are never touched.
 */
function reconcile(
  pending: readonly BufferGeometry[],
  allLoaded: readonly BufferGeometry[],
  tol: number,
): Set<BufferGeometry> {
  const changed = new Set<BufferGeometry>();
  if (pending.length === 0 || allLoaded.length < 2) return changed;

  const hash = new BorderVertexHash(tol);
  for (const geometry of allLoaded) {
    const pos = geometry.getAttribute('position');
    if (!pos) continue;
    const normal = ensureNormals(geometry);
    for (const index of borderVertexIndices(geometry, tol)) {
      hash.insert({ geometry, normal, index, x: pos.getX(index), y: pos.getY(index), z: pos.getZ(index) });
    }
  }

  for (const geometry of pending) {
    const pos = geometry.getAttribute('position');
    if (!pos) continue;
    for (const index of borderVertexIndices(geometry, tol)) {
      const group = hash.query(pos.getX(index), pos.getY(index), pos.getZ(index));
      if (group.length < 2) continue; // unmatched border vertex: leave untouched
      const blended = blendGroup(group);
      if (!blended) continue; // degenerate: keep each side's own normal
      const [bx, by, bz] = blended;
      for (const ref of group) {
        if (
          Math.abs(ref.normal.getX(ref.index) - bx) > ALREADY_EQUAL_EPS ||
          Math.abs(ref.normal.getY(ref.index) - by) > ALREADY_EQUAL_EPS ||
          Math.abs(ref.normal.getZ(ref.index) - bz) > ALREADY_EQUAL_EPS
        ) {
          ref.normal.setXYZ(ref.index, bx, by, bz);
          changed.add(ref.geometry);
        }
      }
    }
  }

  for (const geometry of changed) {
    (geometry.getAttribute('normal') as BufferAttribute).needsUpdate = true;
  }
  return changed;
}

/**
 * Reconcile `target`'s border normals against `neighbours`, blending each
 * shared border vertex to a single normal held by both sides. Thin wrapper over
 * {@link reconcilePendingTiles} for the single-new-tile case; `target` is
 * always part of every group it participates in.
 */
export function reconcileTileNormals(
  target: BufferGeometry,
  neighbours: readonly BufferGeometry[],
  tol = BORDER_MATCH_TOLERANCE_M,
): Set<BufferGeometry> {
  return reconcile([target], [target, ...neighbours], tol);
}

/**
 * Reconcile the border normals of the `pending` geometries (typically the tiles
 * newly attached this tile-update tick) against the whole currently-loaded set
 * `allLoaded` (which includes the pending ones). Order-independent and
 * idempotent, including where three or four tiles meet at a corner. Returns the
 * geometries whose normals changed.
 */
export function reconcilePendingTiles(
  pending: readonly BufferGeometry[],
  allLoaded: readonly BufferGeometry[],
  tol = BORDER_MATCH_TOLERANCE_M,
): Set<BufferGeometry> {
  return reconcile(pending, allLoaded, tol);
}
